import React, { useCallback, useMemo, useState } from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import { useSelector } from '../../../hooks/useSelector';
import ReviewerPortfolioPopup from '../../elements/reviewerPortfolioPopup/ReviewerPortfolioPopup';
import { createPortal } from 'react-dom';
import ReviewedProposal from './reviewedProposal/ReviewedProposal';
import UnreviewedProposal from './unreviewedProposal/UnreviewedProposal';
import AddUserToTeamPopup from '../../elements/addUserToTeamPopup/AddUserToTeamPopup';
import PropTypes from 'prop-types';
import { ReactComponent as HireArrowIcon } from './/hire-icon.svg';
import classNames from 'classnames';

import './PublishedProposals.css';
import { postStatuses } from '../Post';
import { updatePost } from '../../../firebase/api/post';
import { useDispatch } from 'react-redux';
import { useActions } from '../../../hooks/useActions';
import { sendGridEmail } from '../../../firebase/api/mail';
import { getHireToAnswer, getNotHireToAnswer } from '../../../templates';
import { useAuth } from '../../../hooks/useAuth';

const PublishedProposals = ({ color }) => {
    const { data, reviewsData } = useSelector(state => state.post);
    const rawPostData = _cloneDeep(data);
    const [loading, setLoading] = useState(false);
    const [isOpenPortfolio, setIsOpenPortfolio] = useState(false);
    const [isOpenAddToTeam, setIsOpenAddToTeam] = useState(false);
    const [portfolio, setPortfolio] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [pickedFreelancers, setPickedFreelancers] = useState([]);
    const clearPortfolio = useCallback(() => setPortfolio(null), [setPortfolio]);
    const clearUserInfo = useCallback(() => setUserInfo(null), [setUserInfo]);
    const dispatch = useDispatch();
    const { setAssignTo } = useActions();
    const { currentUser } = useAuth();

    const handleTogglePortfolioPopup = useCallback(() => setIsOpenPortfolio(prev => !prev), [
        setIsOpenPortfolio,
    ]);

    const handleToggleAddToTeamPopup = useCallback(() => setIsOpenAddToTeam(prev => !prev), [
        setIsOpenAddToTeam,
    ]);

    const handleOpenPortfolioPopup = useCallback(
        portfolio => {
            setPortfolio(portfolio);
            handleTogglePortfolioPopup();
        },
        [handleTogglePortfolioPopup]
    );

    const handleOpenAddToTeamPopup = useCallback(
        userInfo => {
            setUserInfo(userInfo);
            handleToggleAddToTeamPopup();
        },
        [handleToggleAddToTeamPopup]
    );

    const unreviewedListRender = useCallback(() => {
        if (rawPostData.unreviewed) {
            rawPostData.unreviewed.sort((a, b) => {
                if (rawPostData.viewed.filter(viewedUser => viewedUser.email === a.email).length) {
                    return -1;
                } else if (
                    rawPostData.viewed.filter(viewedUser => viewedUser.email === b.email).length
                ) {
                    return 1;
                } else return 1;
            });
        }

        return rawPostData.unreviewed.map(unreview => (
            <UnreviewedProposal
                key={unreview.uid}
                unreview={unreview}
                rawPostData={rawPostData}
                handleOpenAddToTeamPopup={handleOpenAddToTeamPopup}
            />
        ));
    }, [rawPostData, handleOpenAddToTeamPopup]);

    // const reviewedListRender = useCallback(() => {
    //     return reviewsData.map(review => (
    //         <ReviewedProposal
    //             key={review.publisherUid}
    //             review={review}
    //             data={data}
    //             rawPostData={rawPostData}
    //             loading={loading}
    //             setLoading={setLoading}
    //             handleOpenPortfolioPopup={handleOpenPortfolioPopup}
    //             handleOpenAddToTeamPopup={handleOpenAddToTeamPopup}
    //             color={color}
    //             pickedFreelancers={pickedFreelancers}
    //             // isDisabled={isDisabled}
    //             // buttonHireClassName={buttonHireClassName}
    //             // style={style}
    //             // handleHireClick={handleHireClick}
    //         />
    //     ));
    // }, [
    //     data,
    //     reviewsData,
    //     loading,
    //     color,
    //     handleOpenAddToTeamPopup,
    //     handleOpenPortfolioPopup,
    //     rawPostData,
    //     pickedFreelancers,
    // ]);

    const style = {
        backgroundColor: !loading ? color : '#BDBFC3',
        cursor: 'pointer',
    };

    const confirmHirinsBtnClass = classNames({
        'proposal__hire-button': true,
        'done-hiring-btn': true,
        'disabled-hiring-btn': !pickedFreelancers.length,
    });

    const isPickedAllFreelancers = useMemo(
        () =>
            pickedFreelancers.length ===
            data.responseEmails.filter(review => review.response === 'accept').length,
        [data, pickedFreelancers]
    );

    const handleHireAllClick = useCallback(() => {
        setPickedFreelancers(
            reviewsData
                .filter(review => review.response === 'accept')
                .map(review => review.publisherUid)
        );
    }, [reviewsData]);

    const handleConfirmHiring = useCallback(async () => {
        setLoading(prevValue => !prevValue);
        const assignedFreelancers = reviewsData
            .filter(review => pickedFreelancers.includes(review.publisherUid))
            .map(review => ({
                email: review.publisherEmail,
                uid: review.publisherUid,
                firstName: review.publisherFirstName || review.publisherFullName?.split(' ')[0], // rest for compatibility,
                lastName: review?.publisherLastName || review.publisherFullName?.split(' ')[1],
            }));
        rawPostData.assignTo = assignedFreelancers;
        rawPostData.status = postStatuses.inProgress;
        rawPostData.hasUpdates = false;

        await updatePost(rawPostData).then(() => {
            setLoading(prevValue => !prevValue);
            dispatch(setAssignTo(assignedFreelancers));
            setPickedFreelancers([]);

            sendGridEmail(
                getHireToAnswer(
                    currentUser.email,
                    rawPostData.title,
                    assignedFreelancers,
                    currentUser.firstName,
                    currentUser.lastName,
                    `${window.location.origin}/posts/${rawPostData.id}`,
                    rawPostData.category
                )
            );
            const notHiredRecipients = reviewsData
                .filter(
                    reviewData =>
                        reviewData.response === 'accept' &&
                        !pickedFreelancers.includes(reviewData.publisherUid)
                )
                .map(item => ({
                    email: item.publisherEmail,
                    firstName: item?.pubisherFirstName || item.publisherFullName.split(' ')[0], // rest for compatibility
                    lastName: item?.publisherLastName || item.publisherFullName.split(' ')[1],
                    uid: item.publisherUid,
                }));
            !!notHiredRecipients.length &&
                sendGridEmail(
                    getNotHireToAnswer(
                        currentUser.email,
                        notHiredRecipients,
                        currentUser.firstName,
                        currentUser.lastName,
                        `${window.location.origin}/posts/${rawPostData.id}`,
                        rawPostData.category
                    )
                );
        });
    }, [currentUser, pickedFreelancers, rawPostData, dispatch, setAssignTo, reviewsData]);

    const handleChangePickedFreelancers = useCallback(
        id => {
            if (pickedFreelancers.includes(id)) {
                setPickedFreelancers(prev => prev.filter(freelancerId => freelancerId !== id));
            } else setPickedFreelancers(prev => [...prev, id]);
        },
        [pickedFreelancers]
    );

    const hireAllBtnClass = classNames({
        'proposal__hire-button': true,
        'disabled__hire-all-btn': isPickedAllFreelancers,
    });

    return (
        <>
            <div className="post__proposals-wrap">
                {(!!rawPostData.unreviewed.length || !!reviewsData.length) && (
                    <>
                        <div className="post__proposals-header_wrap">
                            <h3 className="post__proposals-header">Proposals</h3>
                            {!data.assignTo && (
                                <button
                                    style={!data.assignTo && !isPickedAllFreelancers ? style : null}
                                    className={hireAllBtnClass}
                                    onClick={handleHireAllClick}
                                    disabled={isPickedAllFreelancers}
                                >
                                    <span>Select All</span>
                                    <HireArrowIcon />
                                </button>
                            )}
                        </div>
                        <ul className="post__proposals-container project-page__post-content-wrapper">
                            {/* {reviewedListRender()} */}
                            {reviewsData.map(review => (
                                <ReviewedProposal
                                    key={review.publisherUid}
                                    review={review}
                                    data={data}
                                    loading={loading}
                                    handleOpenPortfolioPopup={handleOpenPortfolioPopup}
                                    handleOpenAddToTeamPopup={handleOpenAddToTeamPopup}
                                    pickedFreelancers={pickedFreelancers}
                                    handleChangePickedFreelancers={handleChangePickedFreelancers}
                                    color={color}
                                />
                            ))}
                            {unreviewedListRender()}
                        </ul>

                        {!data.assignTo && (
                            <div className="post__proposals-footer_wrap">
                                <div className="footer__text-note">
                                    You can hire multiple users and we&apos;ll connect you via
                                    email. When you&apos;ve found &apos;the one&apos;, click this
                                    button here.
                                </div>
                                <button
                                    onClick={handleConfirmHiring}
                                    className={confirmHirinsBtnClass}
                                    disabled={!pickedFreelancers.length}
                                >
                                    Done Hiring
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            {isOpenPortfolio &&
                createPortal(
                    <ReviewerPortfolioPopup
                        isOpen={isOpenPortfolio}
                        handleToggle={handleTogglePortfolioPopup}
                        portfolio={portfolio}
                        clearPortfolio={clearPortfolio}
                    />,
                    document.body
                )}
            {isOpenAddToTeam &&
                createPortal(
                    <AddUserToTeamPopup
                        isOpen={isOpenAddToTeam}
                        handleToggle={handleToggleAddToTeamPopup}
                        userEmail={userInfo}
                        clearUserInfo={clearUserInfo}
                    />,
                    document.body
                )}
        </>
    );
};

PublishedProposals.propTypes = {
    color: PropTypes.string.isRequired,
};

export default React.memo(PublishedProposals);
